import type { PropsWithChildren } from 'react';

import { ThemeProvider } from 'styled-components';

import { B2BLayout, ThemeCmsConstellation, ThemeSkunkWorksCmsConstellation } from '@zillow/zrs-cms';

import { LAYOUT_MENU_ITEM_IDS, LAYOUT_MENU_ITEMS } from '../constants/layout';
import RentalsLogo from '../images/rentals-logo.svg';
import RentalsLogoVR from '../images/rentals-logo-vr.svg';

type SubnavItem = {
  id: number;
  title: { rendered: string };
  url: string;
  parent: number;
};

export type Subnav = {
  items: Array<SubnavItem>;
  meta: {
    title: string;
    url: string;
    search: string;
  };
};

interface Props {
  router: {
    asPath: string;
    pathname: string;
  };
  env?: string;
  submenu?: Subnav;
  header?: { disabled?: boolean; transparent?: boolean; fromApp?: boolean };
  useVisualRefreshTheme?: boolean;
}

export const RentalsB2BLayout = ({
  children,
  router,
  env = 'production',
  submenu,
  header,
  useVisualRefreshTheme = false,
}: PropsWithChildren<Props>): JSX.Element => {
  // Select theme and logo based on useVisualRefreshTheme flag
  const theme = useVisualRefreshTheme ? ThemeSkunkWorksCmsConstellation : ThemeCmsConstellation;
  const selectedLogo = useVisualRefreshTheme ? RentalsLogoVR : RentalsLogo;

  // need to check if RentalsLogo is a string or an object; it is different in zillow-rental-manager-web v. zrm-marketing-web
  const img =
    typeof selectedLogo === 'string'
      ? { src: selectedLogo, width: 215, height: 25, blurHeight: 0, blurWidth: 0 }
      : selectedLogo;
  return (
    <ThemeProvider theme={theme}>
      <B2BLayout
        fromApp={header?.fromApp}
        router={router}
        env={env}
        signInLink="/rental-manager/login/"
        signInRedirectParam="url"
        menu={LAYOUT_MENU_ITEMS}
        subnavItems={submenu?.items?.map((item) => ({
          ...item,
          title: item.title.rendered,
        }))}
        subnavPrimaryItem={
          submenu?.items?.[0]
            ? {
                ...submenu.items[0],
                title: submenu.items[0].title.rendered,
              }
            : undefined
        }
        searchPrefix={submenu?.meta?.search}
        transparent={header?.transparent}
        disabled={header?.disabled}
        primaryCTALabel="Post your listing"
        primaryCTALink="/rental-manager/properties?postingPath=true"
        secondaryCTALabel="Contact sales"
        secondaryCTALink="/rentals-network/contact-sales/"
        logo={{
          ...img,
          alt: 'Zillow Rentals Logo',
          link: '/rental-manager/',
        }}
        footerColumns={[
          {
            title: 'Rental manager',
            links: [
              ...LAYOUT_MENU_ITEMS.filter((item) => item.id === LAYOUT_MENU_ITEM_IDS.RentalManager).map(
                convertMenuItemToFooterLink,
              ),
              ...LAYOUT_MENU_ITEMS.filter((item) => item.parent === LAYOUT_MENU_ITEM_IDS.RentalManager).map(
                convertMenuItemToFooterLink,
              ),
            ],
          },
          {
            title: 'Apartment advertising',
            links: [
              ...LAYOUT_MENU_ITEMS.filter((item) => item.id === LAYOUT_MENU_ITEM_IDS.ApartmentAdvertising).map(
                convertMenuItemToFooterLink,
              ),
              ...LAYOUT_MENU_ITEMS.filter((item) => item.parent === LAYOUT_MENU_ITEM_IDS.ApartmentAdvertising).map(
                convertMenuItemToFooterLink,
              ),
            ],
          },
        ]}
      >
        {children}
      </B2BLayout>
    </ThemeProvider>
  );
};

function convertMenuItemToFooterLink(item: (typeof LAYOUT_MENU_ITEMS)[number]) {
  return {
    title: item.parent === 0 ? 'Overview' : item.title.rendered,
    url: item.url,
  };
}
